import { Card, Chip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging } from '@kheops/utils';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import DiscountChip from '../../common/components/DiscountChip';
import i18n from '../../i18n';
import { ellipsisStyle } from '../../common/utils/style.utils';
import { PackagingDetail } from './PackagingPricesDetails';

export interface PackagingPricesCardProps {
  packaging: PackagingDetail;
}

export default function PackagingPricesCard({ packaging }: PackagingPricesCardProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  return (
    <Card
      sx={{
        p: 1.5,
        mb: 1.5,
        boxSizing: 'border-box',
        boxShadow: 'unset',
        backgroundColor: 'surfaceContainerHigh.main',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
        }}
      >
        <Box>
          {!!packaging.discount && (
            <Box>
              <DiscountChip discount={packaging.discount} />
            </Box>
          )}
          <SizedImage
            src={FileUtils.BuildPackagingImageUrl({
              photo: packaging.base_unit.main_photo,
              type: packaging.product.sub_family,
              size: { height: 94, fit: 'contain' },
            })}
            height={94}
            width={94}
            borderRadius={0}
            sx={{
              mt: 1,
              maxHeight: 94,
              maxWidth: 94,
            }}
            alt={t('common:product_one')}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Typography variant="bodyMedium" sx={{ ...ellipsisStyle }}>
              {packaging.product.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Chip
              label={FormatBaseUnitQuantity(packaging.base_unit, 'fr', numberFormat)}
              variant="tag"
              sx={{ backgroundColor: 'surfaceContainerHighest.main' }}
            />
            <Typography variant="bodySmall" color="secondary">
              {FormatLogisticalPackaging(packaging, 'fr', packaging.base_unit)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1.5, gap: 0.5, pl: 0.5 }}>
            {packaging.discount
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="titleMedium"
                    sx={{
                      color: 'primary.main',
                    }}
                  >
                    {packaging.discountedPrimaryPriceDisplay}
                  </Typography>
                  <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ textDecorationLine: 'line-through' }}>
                    {packaging.primaryPriceDisplay}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="titleMedium"
                  sx={{
                    color: 'primary.main',
                  }}
                >
                  {packaging.primaryPriceDisplay}
                </Typography>
              )}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {packaging.discountedSecondaryPriceDisplay && (
                <Typography variant="bodyMedium" color="onSurfaceVariant">
                  {packaging.discountedSecondaryPriceDisplay}
                </Typography>
              )}
              {packaging.discount
                ? (
                  <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ textDecorationLine: 'line-through' }}>
                    {packaging.secondaryPriceDisplay}
                  </Typography>
                )
                : <Typography variant="bodyMedium">{packaging.secondaryPriceDisplay}</Typography>}
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
